import * as React from "react";
import { Upload, X, Link as LinkIcon, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/hooks/useI18n";
import { searchUnsplashPhotos, UnsplashPhoto } from "@/services/unsplash.service";

// Link di attribution richiesto dalle Unsplash API Guidelines: utm_source/utm_medium
// vanno aggiunti a ogni link verso un profilo fotografo o verso unsplash.com.
function withUnsplashUtm(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}utm_source=Place2Be&utm_medium=referral`;
}

interface UploadMultiImagesProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  itineraryId: string;
  activityId?: string; // Optional: if provided, saves to activities-data path
  label?: string;
  maxImages?: number;
  className?: string;
  hasError?: boolean;
  /** Sola lettura: nasconde i controlli di upload/URL/Unsplash e la rimozione immagini. */
  readOnly?: boolean;
}

const UploadMultiImages = React.forwardRef<HTMLDivElement, UploadMultiImagesProps>(
  ({ images, onImagesChange, itineraryId, activityId, label = "Upload Images", maxImages, className, hasError = false, readOnly = false, ...props }, ref) => {
    const { t } = useI18n();
    const [uploading, setUploading] = React.useState(false);
    const [showUrlInput, setShowUrlInput] = React.useState(false);
    const [imageUrl, setImageUrl] = React.useState('');
    const [addingUrl, setAddingUrl] = React.useState(false);
    const [showUnsplash, setShowUnsplash] = React.useState(false);
    const [unsplashQuery, setUnsplashQuery] = React.useState('');
    const [unsplashResults, setUnsplashResults] = React.useState<UnsplashPhoto[]>([]);
    const [unsplashLoading, setUnsplashLoading] = React.useState(false);
    const [unsplashError, setUnsplashError] = React.useState<string | null>(null);

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      setUploading(true);
      const uploadedUrls: string[] = [];

      try {
        for (const file of Array.from(files)) {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
          // Se è presente activityId, salva in activities, altrimenti in media
          const filePath = activityId
            ? `itineraries/${itineraryId}/activities/${activityId}/media/${fileName}`
            : `itineraries/${itineraryId}/media/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('p2b')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error('Upload error:', error);
            alert(`Failed to upload ${file.name}: ${error.message}`);
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('p2b')
            .getPublicUrl(filePath);

          console.log('Generated public URL:', publicUrl);
          uploadedUrls.push(publicUrl);
        }

        const newImages = [...images, ...uploadedUrls];
        const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
        onImagesChange(finalImages);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('An error occurred while uploading images');
      } finally {
        setUploading(false);
      }
    };

    const handleAddImageUrl = async () => {
      if (!imageUrl.trim()) {
        alert('Please enter an image URL');
        return;
      }

      setAddingUrl(true);
      try {
        // Validate that URL is accessible and is an image
        const response = await fetch(imageUrl, { method: 'HEAD' });
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) {
          alert('Invalid image URL. Please enter a valid image URL.');
          setAddingUrl(false);
          return;
        }

        const newImages = [...images, imageUrl];
        const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
        onImagesChange(finalImages);
        setImageUrl('');
        setShowUrlInput(false);
      } catch (error) {
        console.error('Error validating image URL:', error);
        alert('Could not validate image URL. Please check if the URL is correct and accessible.');
      } finally {
        setAddingUrl(false);
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleAddImageUrl();
      }
    };

    const handleUnsplashSearch = async () => {
      if (!unsplashQuery.trim()) return;

      setUnsplashLoading(true);
      setUnsplashError(null);
      try {
        const { results } = await searchUnsplashPhotos({ query: unsplashQuery, per_page: 12 });
        setUnsplashResults(results);
      } catch (error) {
        console.error('Error searching Unsplash photos:', error);
        setUnsplashError(t('modals.unsplashSearchError'));
      } finally {
        setUnsplashLoading(false);
      }
    };

    const handleUnsplashKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleUnsplashSearch();
      }
    };

    const handleSelectUnsplashPhoto = (photo: UnsplashPhoto) => {
      if (isMaxReached) return;
      const newImages = [...images, photo.url];
      const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
      onImagesChange(finalImages);
    };

    const closeUnsplash = () => {
      setShowUnsplash(false);
      setUnsplashQuery('');
      setUnsplashResults([]);
      setUnsplashError(null);
    };

    const removeImage = async (index: number) => {
      const imageUrl = images[index];

      // Try to delete from storage if it's a Supabase URL
      if (imageUrl && imageUrl.includes('supabase')) {
        try {
          // Extract file path from URL
          const urlParts = imageUrl.split('/storage/v1/object/public/p2b/');
          if (urlParts.length > 1) {
            const filePath = urlParts[1];
            await supabase.storage
              .from('p2b')
              .remove([filePath]);
          }
        } catch (error) {
          console.error('Error deleting image from storage:', error);
        }
      }

      const newImages = images.filter((_, i) => i !== index);
      onImagesChange(newImages);
    };

    const isMaxReached = maxImages ? images.length >= maxImages : false;

    return (
      <div ref={ref} className={className} {...props}>
        {!readOnly && (
        <div>
          <Label htmlFor="image-upload" className={`text-sm ${hasError ? "text-yellow-600" : ""}`}>{label}</Label>
          <div className="mt-2 flex gap-2">
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              disabled={isMaxReached}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => document.getElementById('image-upload')?.click()}
              disabled={isMaxReached || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading
                ? t('modals.uploadingImages')
                : isMaxReached
                  ? t('modals.maxImages', { count: maxImages })
                  : t('modals.chooseImages')}
            </Button>

            {/* Add from URL Button */}
            {!showUrlInput && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMaxReached}
                onClick={() => setShowUrlInput(true)}
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                {t('modals.addFromUrl')}
              </Button>
            )}

            {/* Search Unsplash Button */}
            {!showUnsplash && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMaxReached}
                onClick={() => setShowUnsplash(true)}
              >
                <Search className="mr-2 h-4 w-4" />
                {t('modals.searchUnsplash')}
              </Button>
            )}
          </div>

          {/* URL Input Section */}
          {showUrlInput && (
            <div className="mt-3 space-y-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-700">{t('modals.addImageFromUrl')}</span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={addingUrl}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleAddImageUrl}
                  disabled={addingUrl || !imageUrl.trim()}
                >
                  {addingUrl ? t('modals.adding') : t('common.add')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setImageUrl(''); setShowUrlInput(false); }}
                  disabled={addingUrl}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Unsplash Search Section */}
          {showUnsplash && (
            <div className="mt-3 space-y-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-700">{t('modals.searchUnsplash')}</span>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={closeUnsplash}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder={t('modals.unsplashSearchPlaceholder')}
                  value={unsplashQuery}
                  onChange={(e) => setUnsplashQuery(e.target.value)}
                  onKeyPress={handleUnsplashKeyPress}
                  disabled={unsplashLoading}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleUnsplashSearch}
                  disabled={unsplashLoading || !unsplashQuery.trim()}
                >
                  {unsplashLoading ? t('common.loading') : t('common.search')}
                </Button>
              </div>

              {unsplashError && (
                <p className="text-sm text-red-600">{unsplashError}</p>
              )}

              {!unsplashLoading && !unsplashError && unsplashResults.length === 0 && unsplashQuery && (
                <p className="text-sm text-gray-500">{t('modals.unsplashNoResults')}</p>
              )}

              {unsplashResults.length > 0 && (
                <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                  {unsplashResults.map((photo) => (
                    <div
                      key={photo.id}
                      role="button"
                      tabIndex={isMaxReached ? -1 : 0}
                      onClick={() => handleSelectUnsplashPhoto(photo)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSelectUnsplashPhoto(photo); }}
                      aria-disabled={isMaxReached}
                      className={`relative group rounded-md overflow-hidden border border-gray-200 ${
                        isMaxReached ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'
                      }`}
                    >
                      <img src={photo.thumbUrl} alt={photo.description ?? ''} className="w-full h-20 object-cover" />
                      {/* Attribution richiesta dalle Unsplash API Guidelines */}
                      <a
                        href={withUnsplashUtm(photo.author.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate hover:underline"
                        title={t('modals.unsplashPhotoBy', { name: photo.author.name })}
                      >
                        {photo.author.name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Images Preview — horizontal scrollable row */}
        {images && images.length > 0 ? (
          <div className="flex flex-row gap-2 mt-4 overflow-x-auto pb-1">
            {images.map((url, index) => (
              <div key={index} className="relative group flex-shrink-0">
                <img
                  src={url}
                  alt={`Image ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg border"
                />
                {!readOnly && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="absolute top-1 right-1 w-5 h-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          readOnly && <p className="text-sm text-gray-400 italic">{t('modals.noImages')}</p>
        )}
      </div>
    );
  }
);

UploadMultiImages.displayName = "UploadMultiImages";

export { UploadMultiImages };
